import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Box, Typography, Chip, Skeleton, Modal, Button, Divider,
  Stack, Alert as MuiAlert,
} from '@mui/material';
import ArrowUpwardRoundedIcon   from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import OpenInNewRoundedIcon      from '@mui/icons-material/OpenInNewRounded';
import { SparkLineChart }        from '@mui/x-charts/SparkLineChart';
import { areaElementClasses }    from '@mui/x-charts/LineChart';
import { useGrowSetup }          from '../../GrowSetupContext';
import { AuthContext }           from '../../components/signin/AuthContext';
import { useConfig }             from '../../components/ConfigContext';
import SmallConfigTemperatur     from '../../components/SmallConfigTemperatur';
import SmallConfigLicht          from '../../components/SmallConfigLicht';
import SmallConfigAbluft         from '../../components/SmallConfigAbluft';
import SmallConfigCo2            from '../../components/SmallConfigCo2';

/* --- helpers --- */
function getStatus(value, ideal) {
  const v = Number(value), t = Number(ideal);
  if (!isFinite(v) || !isFinite(t) || t === 0 || v === 0) return 'unknown';
  const dev = Math.abs(v - t) / Math.abs(t);
  if (dev >= 0.2) return 'bad';
  if (dev >= 0.1) return 'warn';
  return 'ok';
}

const STATUS_META = {
  ok:      { color: '#22c55e', label: 'Im Zielbereich',      chipColor: 'success' },
  warn:    { color: '#f59e0b', label: 'Leicht abweichend',   chipColor: 'warning' },
  bad:     { color: '#ef4444', label: 'Stark abweichend',    chipColor: 'error'   },
  unknown: { color: '#64748b', label: 'Kein Wert',           chipColor: 'default' },
};

const TREND_META = {
  up:      { color: '#22c55e', Icon: ArrowUpwardRoundedIcon   },
  down:    { color: '#ef4444', Icon: ArrowDownwardRoundedIcon },
  neutral: { color: '#64748b', Icon: HorizontalRuleRoundedIcon},
};

/* animated count-up */
function useCountUp(target, duration = 800) {
  const [display, setDisplay] = useState(target);
  const prev = useRef(target);
  useEffect(() => {
    if (target == null || target === prev.current) return;
    const start = prev.current ?? 0;
    const diff  = target - start;
    const steps = Math.ceil(duration / 16);
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplay(+(start + diff * (i / steps)).toFixed(2));
      if (i >= steps) { clearInterval(id); prev.current = target; }
    }, 16);
    return () => clearInterval(id);
  }, [target]);
  return display;
}

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
        <stop offset="100%" stopColor={color} stopOpacity={0}    />
      </linearGradient>
    </defs>
  );
}

export default function SensorCard({ measure, title, trend, ideal_val, gauge_value, einheit_zeichen, animDelay = 0 }) {
  const { grow_setup }  = useGrowSetup();
  const { token }       = useContext(AuthContext);
  const { state: cfgState } = useConfig();
  const configReady = typeof cfgState.config.sun_rise === 'string';
  const [chartData, setChartData] = useState([]);
  const [xaxis,     setXaxis]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [open,      setOpen]      = useState(false);

  const status    = getStatus(gauge_value, ideal_val);
  const statusMeta = STATUS_META[status];
  const trendMeta  = TREND_META[trend] ?? TREND_META.neutral;
  const gradientId = useMemo(() => `v3-grad-${measure}`, [measure]);
  const displayVal = useCountUp(gauge_value != null ? Number(gauge_value) : null);

  useEffect(() => {
    if (!grow_setup) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const end   = new Date();
    const start = new Date(end.getTime() - 24 * 3600 * 1000);
    axios.post('/get_measures_by_timedelta/', {
      start_time: start, end_time: end,
      grow_setup_id: grow_setup, aggregation_level: '5m', measure,
    }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(res => {
      if (cancelled) return;
      const row  = res?.data?.[0];
      const data = row?.data  ?? [];
      const rawX = row?.xaxis ?? [];
      const fmt  = ts => { const d = new Date(ts); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
      setChartData(data);
      setXaxis(rawX.map(fmt));
    }).catch(e => {
      if (!cancelled) { setErr('Daten nicht verfügbar'); setChartData([]); }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [grow_setup, measure]);

  const pulseKf = status !== 'unknown' ? `
    @keyframes cardPulse_${measure} {
      0%,100% { box-shadow: 0 0 0 0 ${statusMeta.color}44; }
      50%      { box-shadow: 0 0 0 8px ${statusMeta.color}00; }
    }` : '';

  return (
    <>
      {pulseKf && <style>{pulseKf}</style>}
      <Box
        sx={{
          borderRadius: '16px',
          border: `1px solid ${statusMeta.color}33`,
          background: `linear-gradient(145deg,rgba(15,28,15,0.9) 0%,rgba(8,18,10,0.85) 100%)`,
          backdropFilter: 'blur(16px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxShadow: `0 4px 24px ${statusMeta.color}22, inset 0 0 0 1px ${statusMeta.color}1a`,
          animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: `0 8px 32px ${statusMeta.color}33, inset 0 0 0 1px ${statusMeta.color}2a`,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                {title || measure}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                  {gauge_value == null || gauge_value === 0
                    ? '—'
                    : `${displayVal != null ? (Number.isInteger(displayVal) ? displayVal : Number(displayVal).toFixed(1)) : '—'}${einheit_zeichen ?? ''}`}
                </Typography>
                <trendMeta.Icon fontSize="small" sx={{ color: trendMeta.color, mt: 0.2 }} />
              </Box>
              {ideal_val != null && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.25, display: 'block' }}>
                  Ziel: {ideal_val}{einheit_zeichen ?? ''}
                </Typography>
              )}
            </Box>
            <Box>
              <Chip
                size="small"
                label={statusMeta.label}
                color={statusMeta.chipColor}
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: '0.68rem' }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Status bar */}
        <Box sx={{ height: 2, background: `linear-gradient(90deg,${statusMeta.color} 0%,${statusMeta.color}00 100%)`, mx: 2.5, borderRadius: 1, mb: 1 }} />

        {/* Chart */}
        <Box sx={{ px: 2.5, flex: 1, minHeight: 70 }}>
          {loading ? (
            <Skeleton variant="rounded" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
          ) : err ? (
            <Box sx={{ height: 60, display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>{err}</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 70 }}>
              <SparkLineChart
                data={chartData}
                xAxis={{ scaleType: 'band', data: xaxis }}
                colors={[statusMeta.color]}
                area showHighlight showTooltip
                valueFormatter={v => v == null ? '' : Number(v).toFixed(2)}
                sx={{
                  [`& .${areaElementClasses.root}`]: { fill: `url(#${gradientId})` },
                  '& .MuiChartsAxis-root': { display: 'none' },
                }}
              >
                <AreaGradient color={statusMeta.color} id={gradientId} />
              </SparkLineChart>
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, mt: 1 }} />

        {/* Footer */}
        <Box sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            endIcon={<OpenInNewRoundedIcon fontSize="inherit" />}
            onClick={() => setOpen(true)}
            sx={{ color: '#76c442', fontWeight: 600, fontSize: '0.75rem', '&:hover': { background: 'rgba(118,196,66,0.1)' } }}
          >
            Details
          </Button>
        </Box>
      </Box>

      {/* Detail Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 440, maxWidth: '92vw',
          background: 'rgba(8,20,9,0.97)',
          border: '1px solid rgba(118,196,66,0.2)',
          borderRadius: '18px',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          p: 3,
        }}>
          {configReady && measure === 'licht'      && <SmallConfigLicht />}
          {configReady && measure === 'humidity'   && <SmallConfigAbluft />}
          {configReady && (measure === 'temperatur' || measure === 'temperatur_2') && <SmallConfigTemperatur />}
          {configReady && measure === 'co2'        && <SmallConfigCo2 />}
          {configReady && !['licht','humidity','temperatur','temperatur_2','co2'].includes(measure) && (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', py: 2 }}>
              Keine Detailkonfiguration verfügbar.
            </Typography>
          )}
          {!configReady && <Typography sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 4 }}>Laden…</Typography>}
          <Button fullWidth variant="outlined" onClick={() => setOpen(false)} sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>
            Schließen
          </Button>
        </Box>
      </Modal>

      <style>{`
        @keyframes fadeSlideUpCard {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0);    }
        }
      `}</style>
    </>
  );
}

SensorCard.propTypes = {
  measure:        PropTypes.string.isRequired,
  title:          PropTypes.string,
  trend:          PropTypes.oneOf(['up','down','neutral']),
  ideal_val:      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gauge_value:    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  einheit_zeichen: PropTypes.string,
  animDelay:      PropTypes.number,
};
